import { useCallback, useState } from 'preact/hooks';
import type { Product } from '../../../models/Product';
import Button from '../../button/Button';
import './MultiProductVariantPicker.less';
import ProductVariantPicker from './ProductVariantPicker';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatCurrency } from '../../../services/FormatterService';
import ProductMiniature from '../miniature/ProductMiniature';
import ProductPrice from '../ProductPrice';

export type MultiProductVariantPickerProps = {
    products: Product[];
    isLoading?: boolean;
    onAccept: (items: { productId: string; variantId: string }[]) => void;
    onRemove: (productId: string) => void;
};

const MultiProductVariantPicker = ({
    products,
    isLoading,
    onAccept,
    onRemove
}: MultiProductVariantPickerProps) => {
    const { t } = useTranslation();
    const [selectedVariants, setSelectedVariants] = useState<
        { productId: string; variantId: string | undefined }[]
    >(products.map(p => ({ productId: p.productId, variantId: undefined })));
    const onChangeVariant = useCallback(
        (productId: string, variantId: string | undefined) => {
            setSelectedVariants(selectedVariants =>
                selectedVariants.map(sv => {
                    return sv.productId === productId
                        ? {
                              productId,
                              variantId
                          }
                        : sv;
                })
            );
        },
        []
    );
    const areAllVariantsSelected = selectedVariants
        .filter(v => products.some(p => p.productId === v.productId))
        .every(v => v.variantId);
    if (products.length === 0) {
        return null;
    }
    return (
        <div className={`bri-MultiProductVariantPicker`}>
            <div
                className={`bri-MultiProductVariantPicker-products-wrapper bri-SlimScroll`}
            >
                <div className={`bri-MultiProductVariantPicker-products`}>
                    {products.map((product, index) => (
                        <>
                            <div
                                className={`bri-MultiProductVariantPicker-product`}
                            >
                                <ProductMiniature
                                    size="xx-large"
                                    aspectRatio="1"
                                    product={product}
                                    onRemove={
                                        products.length > 1
                                            ? () => onRemove(product.productId)
                                            : undefined
                                    }
                                />
                                <div className="bri-MultiProductVariantPicker-product-info">
                                    <div className="bri-MultiProductVariantPicker-product-text">
                                        <div className={'bri-TitleBoldFont'}>
                                            {product.name}
                                        </div>
                                        <ProductPrice
                                            className="bri-DisplayBoldFont"
                                            product={product}
                                        />
                                    </div>
                                    <ProductVariantPicker
                                        product={product}
                                        isLoading={isLoading}
                                        onChange={onChangeVariant}
                                        showSingleVariant={products.length > 1}
                                    />
                                </div>
                            </div>
                            {index < products.length - 1 && (
                                <div className="bri-MultiProductVariantPicker-divider"></div>
                            )}
                        </>
                    ))}
                </div>
            </div>
            <div className={`bri-MultiProductVariantPicker-footer-wrapper`}>
                <div className={`bri-MultiProductVariantPicker-footer`}>
                    <div className={`bri-MultiProductVariantPicker-info`}>
                        <div className={`bri-DisplayFont`}>
                            {t('PRODUCTS_LABEL_MANY', {
                                count: products.length
                            })}
                        </div>
                        <div className={`bri-DisplayBoldFont`}>
                            {formatCurrency(
                                products.reduce(
                                    (sum, p) => sum + p.unitPrice,
                                    0
                                )
                            )}
                        </div>
                    </div>
                    <Button
                        disabled={isLoading || !areAllVariantsSelected}
                        size="large"
                        variant="magic"
                        onClick={() => {
                            areAllVariantsSelected &&
                                onAccept(
                                    selectedVariants as {
                                        productId: string;
                                        variantId: string;
                                    }[]
                                );
                        }}
                    >
                        {t('ADD_TO_CART_BUTTON_LABEL_MANY', {
                            count: products.length
                        })}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MultiProductVariantPicker;
